import React, { useContext, useMemo, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Context } from "../context/ProductsProvider";
import { useAuthContext } from "../context/AuthContext";
import Firestore from "../handlers/firestore";
import Storage from "../handlers/storage";
import Preview from "./Preview";

const { createProduct } = Firestore;
const { uploadFile, downloadFile } = Storage;

const UploadProductForm = () => {

  const { dispatch, state, read } = useContext(Context);
  const { currentUser } = useAuthContext();
  const { isCollapsed: isVisible, inputs, items } = state;

  const [successMessage, setSuccessMessage] = useState(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleOnChange = (e) => dispatch({ type: 'setInputs', payload: { value: e}})

  const username = currentUser?.displayName?.split(" ").join("") || "";

  const navigate = useNavigate();
  const location = useLocation();

  const { id: itemId } = location.state || {};

  useEffect(() => {
    // Reset inputs when the component mounts
    dispatch({ type: "resetInputs" });
  }, [dispatch]);

  useEffect(() => {
    const currentItem = items.find((item) => item.id === itemId);
    if (currentItem) {
      dispatch({ type: "updateInputs", payload: { value: currentItem } });
    }
  }, [dispatch, items, itemId]);

const handleOnSubmit = (e) => {
  e.preventDefault();
  if (isSubmitting) return; // Prevent multiple submissions
  setIsSubmitting(true); // Set submitting state to true

  uploadFile(state.inputs)
    .then(downloadFile)
    .then((url) => {
      
      // Check if product already exists
      const existingProduct = state.items.find(item => item.title === inputs.title);
      if (existingProduct) {
        alert("Product already exists.");
        setSuccessMessage("Product already exists.");
        setIsSubmitting(false); // Reset submitting state
        dispatch({ type: "collapse", payload: { bool: false } })
        return;
      }

      createProduct({ ...inputs, path: url, user: username.toLowerCase() }, "products").then(() => {
        read();
        dispatch({ type: "collapse", payload: { bool: false } });
        setSuccessMessage("Product uploaded successfully!");
        // Clear form inputs
        dispatch({ type: "resetInputs" });
        setIsSubmitting(false); // Reset submitting state
        dispatch({ type: "collapse", payload: { bool: false } })
        navigate("/");
      });
    })

    .catch(error => {
      console.error("Error uploading product:", error);
      setSuccessMessage("Error uploading product.");
      setIsSubmitting(false); // Reset submitting state
      dispatch({ type: "collapse", payload: { bool: false } })
    });
  };

  const isDisabled = useMemo(() => {
    return !!Object.values(inputs).some((input) => input === null || input === '');
  }, [inputs]);

  const handleDismissSuccessMessage = () => {
    setSuccessMessage(null);
    // Clear form inputs
    dispatch({ type: 'setInputs', payload: { value: { title: null, file: null, path: null } } });
  };

  return (
    isVisible && 
      <>
        {successMessage && (
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            {successMessage}
            <button type="button" className="btn-close" aria-label="Close" onClick={handleDismissSuccessMessage}></button>
          </div>
        )}
        <p className="display-6 text-center mb-3">Add New Item</p>
        <div className="mb-5 d-flex align-items-center justify-content-center">
          <Preview />
          <form
            className="mb-2"
            style={{ textAlign: "left" }}
            onSubmit={handleOnSubmit}
          >
            <div className="mb-3">
              <label htmlFor="title" className="form-label">
                Product Name
              </label>
              <input
                type="text"
                className="form-control"
                id="title"
                name="title"
                placeholder="Enter product name"
                value={inputs.title || ''}
                onChange={handleOnChange}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="category" className="form-label">
                Category
              </label>
              <select
                className="form-select"
                id="category"
                name="category"
                value={inputs.category || ''}
                onChange={handleOnChange}
              >
                <option value="">Select Category</option>
                <option value="Shoes">Shoes</option>
                <option value="Wallets">Wallets</option>
                <option value="Bags">Bags</option>
                <option value="Clothes">Clothes</option>
                <option value="Jewellery">Jewellery</option>
                <option value="Glasses">Glasses</option>
                <option value="Watches">Watches</option>
                <option value="Perfumes">Perfumes</option>
              </select>
            </div>
            <div className="mb-3">
              <label htmlFor="subcategory" className="form-label">
              Subcategory
              </label>
              <select
                className="form-select"
                id="subcategory"
                name="subcategory"
                value={inputs.subcategory}
                onChange={handleOnChange}
              >
              <option value="">Select Subcategory</option>
              <optgroup label="Men shoes subcategory">
                <option value="loafers">Loafers</option>
                <option value="sneakers">Sneakers</option>
                <option value="slides">Slides</option>
                <option value="yeezys">Yeezys</option>
              </optgroup>
              <optgroup label="Men bags subcategory">
                <option value="messengers">Messengers</option>
                <option value="duffles">Duffles</option>
                <option value="back-packs">Back Packs</option>
                <option value="slings">Slings</option>
                <option value="satchels">Satchels</option>
                <option value="belt-bags">Belt Bags</option>
              </optgroup>
              <optgroup label="Women shoes subcategory">
                <option value="espadrilles">Espadrilles</option>
                <option value="sneakers">Sneakers</option>
                <option value="slides">Slides</option>
                <option value="heels">Heels</option>
                <option value="sandals">Sandals</option>
              </optgroup>
              <optgroup label="Women bags subcategory">
                <option value="totes">Totes</option>
                <option value="shoulder-cross-body">Shoulder/Cross-Body</option>
                <option value="mini-bags">Mini Bags</option>
                <option value="clutches">Clutches</option>
              </optgroup>
              <option value="no-subcategory">No Subcategory</option>
            </select>
            </div>
            <div className="mb-3">
              <label htmlFor="gender" className="form-label">
                Gender
              </label>
              <select
                className="form-select"
                id="gender"
                name="gender"
                value={inputs.gender || ''}
                onChange={handleOnChange}
              >
                <option value="">Select gender</option>
                <option value="Men">Men</option>
                <option value="Women">Women</option>
                <option value="unisex">unisex</option>
              </select>
            </div>
            <div className="mb-3">
              <label htmlFor="description" className="form-label">
                Description
              </label>
              <textarea
                className="form-control"
                id="description"
                name="description"
                placeholder="Enter product description"
                value={inputs.description || ''}
                onChange={handleOnChange}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="price" className="form-label">
                Price
              </label>
              <input
                type="number"
                className="form-control"
                id="price"
                name="price"
                placeholder="Enter price"
                value={inputs.price || ''}
                onChange={handleOnChange}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="size" className="form-label">
                Size
              </label>
              <input
                type="text"
                className="form-control"
                id="size"
                name="size"
                placeholder="Enter size"
                value={inputs.size || ''}
                onChange={handleOnChange}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="quantity" className="form-label">
                Quantity
              </label>
              <input
                type="number"
                className="form-control"
                id="quantity"
                name="quantity"
                placeholder="Enter quantity"
                value={inputs.quantity || ''}
                onChange={handleOnChange}
              />
            </div>
            <div className="mb-3">
              <label htmlFor="color" className="form-label">
                Color
              </label>
              <input
                type="text"
                className="form-control"
                id="color"
                name="color"
                placeholder="Enter color"
                value={inputs.color || ''}
                onChange={handleOnChange}
              />
            </div>
            <div className="mb-3">
              <input
                type="file"
                className="form-control"
                id="file"
                name="file"
                onChange={(e) =>
                  handleOnChange({
                    target: { name: "file", files: e.target.files },
                  })
                }
              />
            </div>
            <button
              type="submit"
              className="btn btn-success float-end"
              disabled={isDisabled || isSubmitting} // Disable button while submitting
            >
              {isSubmitting ? "Submitting..." : "Save and Upload"}
            </button>
          </form>
        </div>
      </>
  );
};

export default UploadProductForm;
